import React from "react";
import { X, Shield } from "lucide-react";
import moment from "moment";

interface CarteiraMilitarData {
  nick: string;
  patente: string;
  sigla?: string;
  ultima_promocao: string;
  promovido_por: string;
  status: string;
}

interface CarteiraMilitarModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: CarteiraMilitarData | null;
}

export function CarteiraMilitarModal({
  isOpen,
  onClose,
  data,
}: CarteiraMilitarModalProps) {
  if (!isOpen || !data) return null;

  const avatarUrl = `https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${encodeURIComponent(data.nick)}&action=std&direction=2&head_direction=3&img_format=png&gesture=sml&frame=0&headonly=1&size=l`;

  const formatDate = (date: string) => {
    return moment(date).format("DD/MM/YYYY");
  };

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes("ativo")) return "#22c55e";
    if (s.includes("inativo") || s.includes("desligado")) return "#ef4444";
    if (s.includes("férias") || s.includes("ferias") || s.includes("licença") || s.includes("licenca")) return "#f59e0b";
    return "#94a3b8";
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botão fechar */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-7 h-7 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center text-gray-300 hover:text-white hover:bg-gray-700 transition-colors shadow-lg"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Carteira */}
        <div
          className="rounded-xl shadow-2xl overflow-hidden"
          style={{
            background: "linear-gradient(145deg, #1a2e1a 0%, #0d1f0d 50%, #162116 100%)",
            border: "2px solid #2d5a1e",
            boxShadow: "0 0 40px rgba(34, 197, 94, 0.15), 0 20px 60px rgba(0,0,0,0.8)",
          }}
        >
          {/* Cabeçalho da Carteira */}
          <div
            className="px-5 py-4 text-center relative overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #1f3d1f 0%, #163016 100%)",
              borderBottom: "2px solid #2d5a1e",
            }}
          >
            {/* Linhas decorativas de fundo */}
            <div className="absolute inset-0 opacity-5">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full"
                  style={{
                    top: `${i * 14}%`,
                    height: "1px",
                    background: "#22c55e",
                  }}
                />
              ))}
            </div>

            <div className="relative z-10 flex flex-col items-center gap-0.5">
              <Shield className="w-6 h-6 mb-1" style={{ color: "#86efac" }} />
              <p
                className="text-xs font-semibold tracking-widest uppercase"
                style={{ color: "#86efac", letterSpacing: "0.15em" }}
              >
                República Federativa do Habbo
              </p>
              <p
                className="text-xs tracking-wider uppercase"
                style={{ color: "#4ade80", letterSpacing: "0.1em" }}
              >
                Ministério da Defesa
              </p>
              <p
                className="text-sm font-bold tracking-wider uppercase mt-0.5"
                style={{ color: "#22c55e", letterSpacing: "0.12em" }}
              >
                Exército Brasileiro
              </p>
              <div
                className="mt-2 px-4 py-1 rounded"
                style={{
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid #22c55e",
                }}
              >
                <p
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "#bbf7d0", letterSpacing: "0.2em" }}
                >
                  Carteira de Identidade Militar
                </p>
              </div>
            </div>
          </div>

          {/* Corpo da Carteira */}
          <div className="p-5">
            <div className="flex items-start gap-4">
              {/* Foto do Militar */}
              <div className="flex-shrink-0 flex flex-col items-center gap-2">
                <div
                  className="rounded-lg overflow-hidden flex items-center justify-center"
                  style={{
                    width: "80px",
                    height: "90px",
                    background: "linear-gradient(180deg, #1e3a1e 0%, #162516 100%)",
                    border: "2px solid #2d5a1e",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                  }}
                >
                  <img
                    src={avatarUrl}
                    alt={data.nick}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
                {/* Número decorativo */}
                <div
                  className="text-center px-2 py-0.5 rounded text-xs font-mono"
                  style={{
                    background: "rgba(34, 197, 94, 0.1)",
                    border: "1px solid #1e4d1e",
                    color: "#4ade80",
                  }}
                >
                  EXBR
                </div>
              </div>

              {/* Dados do Militar */}
              <div className="flex-1 space-y-2.5">
                {/* Nick */}
                <div>
                  <p
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ color: "#4ade80", letterSpacing: "0.12em" }}
                  >
                    Nome de Guerra
                  </p>
                  <p
                    className="text-base font-bold mt-0.5"
                    style={{ color: "#f0fdf4" }}
                  >
                    {data.nick}
                  </p>
                </div>

                {/* Patente */}
                <div>
                  <p
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ color: "#4ade80", letterSpacing: "0.12em" }}
                  >
                    Posto / Graduação
                  </p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: "#dcfce7" }}>
                    {data.patente}
                    {data.sigla ? (
                      <span className="ml-1.5 text-xs font-normal" style={{ color: "#86efac" }}>
                        {data.sigla}
                      </span>
                    ) : null}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <p
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ color: "#4ade80", letterSpacing: "0.12em" }}
                  >
                    Situação
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ background: getStatusColor(data.status) }}
                    />
                    <p className="text-sm font-semibold" style={{ color: "#dcfce7" }}>
                      {data.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Separador */}
            <div
              className="my-4 w-full"
              style={{
                height: "1px",
                background: "linear-gradient(90deg, transparent, #2d5a1e, transparent)",
              }}
            />

            {/* Informações de promoção */}
            <div className="grid grid-cols-2 gap-3">
              <div
                className="p-2.5 rounded-lg"
                style={{
                  background: "rgba(34, 197, 94, 0.06)",
                  border: "1px solid #1e4d1e",
                }}
              >
                <p
                  className="text-xs uppercase tracking-wider font-semibold mb-1"
                  style={{ color: "#4ade80", letterSpacing: "0.1em" }}
                >
                  Última Promoção
                </p>
                <p className="text-sm font-bold" style={{ color: "#bbf7d0" }}>
                  {formatDate(data.ultima_promocao)}
                </p>
              </div>

              <div
                className="p-2.5 rounded-lg"
                style={{
                  background: "rgba(34, 197, 94, 0.06)",
                  border: "1px solid #1e4d1e",
                }}
              >
                <p
                  className="text-xs uppercase tracking-wider font-semibold mb-1"
                  style={{ color: "#4ade80", letterSpacing: "0.1em" }}
                >
                  Promovido por
                </p>
                <p className="text-sm font-bold" style={{ color: "#bbf7d0" }}>
                  {data.promovido_por}
                </p>
              </div>
            </div>
          </div>

          {/* Rodapé */}
          <div
            className="px-5 py-2.5 flex items-center justify-between"
            style={{
              background: "rgba(0,0,0,0.3)",
              borderTop: "1px solid #1a3d1a",
            }}
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-sm"
                  style={{ background: i < 3 ? "#22c55e" : "#1e4d1e" }}
                />
              ))}
            </div>
            <p
              className="text-xs font-mono"
              style={{ color: "#2d6a2d" }}
            >
              EXBR • DOCUMENTO OFICIAL
            </p>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-sm"
                  style={{ background: i > 1 ? "#22c55e" : "#1e4d1e" }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
